import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  MdAdd,
  MdBadge,
  MdDelete,
  MdEdit,
  MdFirstPage,
  MdLastPage,
  MdNavigateBefore,
  MdNavigateNext,
  MdViewList,
  MdViewModule,
  MdPerson,
  MdEmail,
  MdDateRange,
  MdSearch,
  MdFilterList,
  MdClose,
  MdExpandMore,
  MdExpandLess,
  MdBusiness,
  MdVisibility,
} from "react-icons/md";

/**
 * CardUsers avec pagination backend (API paginée)
 * Nécessite que le parent passe users (page courante), currentPage, perPage, lastPage, total, fetchPage pour piloter la pagination.
 */
const CardUsers = ({
  isOpen,
  users,
  isLoading,
  onEdit,
  onDelete,
  onViewDetails,
  currentPage,
  perPage,
  lastPage,
  total,
  fetchPage,
}) => {
  const [viewMode, setViewMode] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAffiliation, setFilterAffiliation] = useState("");
  const [showFilter, setShowFilter] = useState(false);

  // Uniquement pour filtrer localement les users de la page courante (UX)
  const uniqueAffiliations = [
    ...new Set(
      users
        .flatMap(
          (u) =>
            u.affiliation_managers?.map((am) => am.affiliation?.name_aff) || []
        )
        .filter(Boolean)
    ),
  ];

  // Helpers
  const getUserAffiliations = (user) => {
    if (!user.affiliation_managers || user.affiliation_managers.length === 0) {
      return "Aucune affiliation";
    }
    return user.affiliation_managers
      .map((am) => am.affiliation?.name_aff)
      .filter(Boolean)
      .join(", ");
  };

  const getUserFirstAffiliation = (user) => {
    if (!user.affiliation_managers || user.affiliation_managers.length === 0) {
      return "Aucune affiliation";
    }
    return (
      user.affiliation_managers[0]?.affiliation?.name_aff ||
      "Aucune affiliation"
    );
  };

  // Filtrage local (UX sur la page courante)
  const getFilteredUsers = () => {
    let filtered = users;
    if (searchTerm.trim() !== "") {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.first_name?.toLowerCase().includes(lower) ||
          u.last_name?.toLowerCase().includes(lower) ||
          u.email_user?.toLowerCase().includes(lower) ||
          getUserAffiliations(u).toLowerCase().includes(lower)
      );
    }
    if (filterAffiliation) {
      filtered = filtered.filter((u) =>
        u.affiliation_managers?.some(
          (am) => am.affiliation?.name_aff === filterAffiliation
        )
      );
    }
    return filtered;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""
      }`.toUpperCase();
  };

  // Pagination pilotée par l'API
  const goToPage = (page) => {
    if (page >= 1 && page <= lastPage && fetchPage) {
      fetchPage(page, perPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const value = parseInt(e.target.value);
    if (fetchPage) {
      fetchPage(1, value);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const delta = 2;
    if (lastPage <= 7) {
      for (let i = 1; i <= lastPage; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(lastPage);
      } else if (currentPage >= lastPage - 3) {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = lastPage - 4; i <= lastPage; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = currentPage - delta; i <= currentPage + delta; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(lastPage);
      }
    }
    return pageNumbers;
  };

  const renderPageButton = (pageNum, index) => {
    if (pageNum === "...") {
      return (
        <span
          key={`ellipsis-${index}`}
          className="flex h-10 w-10 items-center justify-center font-medium text-gray-500"
        >
          ...
        </span>
      );
    }
    return (
      <button
        key={pageNum}
        onClick={() => goToPage(pageNum)}
        className={`flex h-10 w-10 items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 ${currentPage === pageNum
          ? "scale-105 transform bg-[#57A863] text-white shadow-lg"
          : "border border-gray-300 bg-white text-gray-700 hover:border-[#57A863] hover:bg-gray-50 hover:text-[#57A863]"
          }`}
      >
        {pageNum}
      </button>
    );
  };

  const renderPagination = () => {
    if (lastPage <= 1) return null;
    const pageNumbers = getPageNumbers();
    const filteredLength = total;
    const startItem = (currentPage - 1) * perPage + 1;
    const endItem = Math.min(currentPage * perPage, filteredLength);

    return (
      <div className="mt-8 space-y-6">
        <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
          <div className="flex items-center space-x-4 text-sm text-gray-700">
            <div className="flex items-center space-x-2">
              <span className="font-medium">Lignes par page:</span>
              <select
                className="rounded-lg border border-gray-300 px-3 py-2 transition-all focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/20"
                value={perPage}
                onChange={handleItemsPerPageChange}
              >
                <option value={6}>6</option>
                <option value={10}>10</option>
                <option value={12}>12</option>
                <option value={18}>18</option>
                <option value={24}>24</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="hidden h-4 w-px bg-gray-300 sm:block"></div>
            <div className="text-gray-600">
              <span className="font-medium text-gray-900">{startItem}</span>
              {" - "}
              <span className="font-medium text-gray-900">{endItem}</span>
              {" sur "}
              <span className="font-semibold text-[#57A863]">
                {filteredLength}
              </span>
              {" entrées"}
            </div>
          </div>
          <div className="text-sm text-gray-600 sm:hidden">
            Page {currentPage} sur {lastPage}
          </div>
        </div>
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition-all hover:border-[#57A863] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:bg-white"
            title="Première page"
          >
            <MdFirstPage className="h-5 w-5" />
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition-all hover:border-[#57A863] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:bg-white"
            title="Page précédente"
          >
            <MdNavigateBefore className="h-5 w-5" />
          </button>
          <div className="hidden items-center space-x-1 sm:flex">
            {pageNumbers.map((pageNum, index) =>
              renderPageButton(pageNum, index)
            )}
          </div>
          <div className="flex items-center rounded-lg bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 sm:hidden">
            {currentPage} / {lastPage}
          </div>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === lastPage}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition-all hover:border-[#57A863] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:bg-white"
            title="Page suivante"
          >
            <MdNavigateNext className="h-5 w-5" />
          </button>
          <button
            onClick={() => goToPage(lastPage)}
            disabled={currentPage === lastPage}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition-all hover:border-[#57A863] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:bg-white"
            title="Dernière page"
          >
            <MdLastPage className="h-5 w-5" />
          </button>
        </div>
        {lastPage > 10 && (
          <div className="flex items-center justify-center space-x-2 text-sm">
            <span className="text-gray-600">Aller à la page:</span>
            <input
              type="number"
              min="1"
              max={lastPage}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= lastPage) {
                  goToPage(page);
                }
              }}
              className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-center focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/20"
            />
            <span className="text-gray-600">sur {lastPage}</span>
          </div>
        )}
      </div>
    );
  };

  const renderGridView = () => (
    <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3">
      {getFilteredUsers().map((reviewer, index) => (
        <div
          key={reviewer.id_rev}
          className="group overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#57A863]/30 hover:shadow-xl"
        >
          <div className="bg-gradient-to-r from-[#57A863] to-[#3d9448] px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 text-lg font-semibold text-white">
                {getInitials(reviewer.first_name, reviewer.last_name)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-lg font-semibold text-white">
                  {reviewer.first_name} {reviewer.last_name}
                </h3>
                <span className="inline-flex rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-gray-700">
                  {reviewer.affiliation_managers?.length || 0} affiliation(s)
                </span>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3 text-sm">
                <MdEmail className="h-5 w-5 flex-shrink-0 text-gray-400" />
                <span className="truncate font-medium text-gray-600">
                  {reviewer.email_user}
                </span>
              </div>
              <div className="flex items-start space-x-3 text-sm">
                <MdBusiness className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-gray-600">
                    {getUserFirstAffiliation(reviewer)}
                  </span>
                  {reviewer.affiliation_managers?.length > 1 && (
                    <span className="mt-1 block text-xs text-gray-500">
                      +{reviewer.affiliation_managers.length - 1} autre(s)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <MdDateRange className="h-5 w-5 flex-shrink-0 text-gray-400" />
                <span className="text-gray-600">
                  {new Date(reviewer.created_at).toLocaleDateString("fr-FR", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>

              <div className="flex items-center space-x-3 text-sm">
                {reviewer.category && (
                  <>
                    <MdDateRange className="h-5 w-5 flex-shrink-0 text-gray-400" />
                    <span className="text-gray-600">
                      {reviewer.category?.name_cat_rev}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-2">
              <button
                className="flex items-center justify-center rounded-lg bg-green-50 px-3 py-2.5 text-sm font-medium text-green-600 transition-all hover:scale-105 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                onClick={() => onViewDetails && onViewDetails(reviewer)}
                title="Voir les détails"
              >
                <MdVisibility className="h-4 w-4" />
              </button>
              <button
                className="flex items-center justify-center rounded-lg bg-blue-50 px-3 py-2.5 text-sm font-medium text-blue-600 transition-all hover:scale-105 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                onClick={() => onEdit(reviewer)}
                title="Modifier"
              >
                <MdEdit className="h-4 w-4" />
              </button>
              <button
                className="flex items-center justify-center rounded-lg bg-red-50 px-3 py-2.5 text-sm font-medium text-red-600 transition-all hover:scale-105 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                onClick={() => onDelete(reviewer)}
                title="Supprimer"
              >
                <MdDelete className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                <div className="flex items-center space-x-1">
                  <MdPerson className="h-4 w-4" />
                  <span>Utilisateur</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                <div className="flex items-center space-x-1">
                  <MdEmail className="h-4 w-4" />
                  <span>Email</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                <div className="flex items-center space-x-1">
                  <MdBusiness className="h-4 w-4" />
                  <span>Affiliations</span>
                </div>
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {getFilteredUsers().map((reviewer, index) => (
              <tr
                key={reviewer.id_rev}
                className={`transition-colors hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  }`}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#57A863] to-[#3d9448] text-sm font-semibold text-white">
                      {getInitials(reviewer.first_name, reviewer.last_name)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {reviewer.first_name} {reviewer.last_name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="max-w-xs break-all text-sm text-gray-900">
                    {reviewer.email_user}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="max-w-xs">
                    <div className="text-sm text-gray-900">
                      {getUserFirstAffiliation(reviewer)}
                    </div>
                    {reviewer.affiliation_managers?.length > 1 && (
                      <div className="text-xs text-gray-500">
                        +{reviewer.affiliation_managers.length - 1} autre(s)
                        affiliation(s)
                      </div>
                    )}
                    <span className="mt-1 inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                      {reviewer.affiliation_managers?.length || 0} total
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end space-x-2">
                    <button
                      className="inline-flex items-center rounded-lg bg-green-50 px-3 py-2 text-xs font-medium text-green-600 transition-colors hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                      onClick={() => onViewDetails && onViewDetails(reviewer)}
                      title="Voir les détails"
                    >
                      <MdVisibility className="mr-1 h-3 w-3" />
                      Détails
                    </button>
                    <button
                      className="inline-flex items-center rounded-lg bg-blue-50 px-3 py-2 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                      onClick={() => onEdit(reviewer)}
                      title="Modifier"
                    >
                      <MdEdit className="mr-1 h-3 w-3" />
                      Modifier
                    </button>
                    <button
                      className="inline-flex items-center rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600 transition-colors hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                      onClick={() => onDelete(reviewer)}
                      title="Supprimer"
                    >
                      <MdDelete className="mr-1 h-3 w-3" />
                      Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="flex h-64 flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 p-8 text-center">
      <div className="rounded-full bg-gray-100 p-3">
        <MdBadge className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900">
        Aucun rélecteur trouvé
      </h3>
      <p className="mt-2 max-w-sm text-sm text-gray-600">
        {searchTerm || filterAffiliation
          ? "Aucun résultat ne correspond à vos critères de recherche."
          : "Commencez par ajouter votre premier rélecteur pour gérer les révisions."}
      </p>
      {!searchTerm && !filterAffiliation && (
        <button
          onClick={isOpen}
          className="mt-6 flex items-center rounded-lg bg-gradient-to-r from-[#57A863] to-[#3d9448] px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2"
        >
          <MdAdd className="mr-2 h-4 w-4" />
          Ajouter un rélecteur
        </button>
      )}
    </div>
  );

  const renderLoadingState = () => (
    <div className="flex h-64 items-center justify-center rounded-xl border border-gray-200 bg-white">
      <div className="text-center">
        <div className="border-t-transparent mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#57A863]"></div>
        <p className="mt-4 text-lg font-medium text-gray-900">
          Chargement des rélecteurs...
        </p>
        <p className="text-sm text-gray-500">Veuillez patienter</p>
      </div>
    </div>
  );

  const renderContentWithData = () => (
    <>
      {viewMode === "grid" ? renderGridView() : renderListView()}
      {renderPagination()}
    </>
  );

  const getMainContent = () => {
    if (isLoading) {
      return renderLoadingState();
    }
    if (getFilteredUsers().length === 0) {
      return renderEmptyState();
    }
    return renderContentWithData();
  };

  const renderSearchAndFilterControls = () => (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex w-full items-center sm:w-auto">
          <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            className="w-full rounded-md border border-gray-300 py-2.5 pl-10 pr-4 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863] sm:w-80"
            placeholder="Recherche par nom, email ou affiliation..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (fetchPage) fetchPage(1, perPage);
            }}
          />
          {searchTerm && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setSearchTerm("")}
              title="Effacer la recherche"
              tabIndex={0}
            >
              <MdClose className="h-5 w-5" />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFilter(!showFilter)}
          className={`flex items-center rounded-md border px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#57A863] ${showFilter
            ? "border-[#57A863] bg-[#57A863] text-white"
            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            }`}
          title="Filtrer"
        >
          <MdFilterList className="mr-2 h-5 w-5" />
          <span className="hidden sm:inline">Filtres avancés</span>
          <span className="sm:hidden">Filtres</span>
          {showFilter ? (
            <MdExpandLess className="ml-1 h-4 w-4" />
          ) : (
            <MdExpandMore className="ml-1 h-4 w-4" />
          )}
        </button>
      </div>
      {showFilter && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 transition-all duration-200">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label
                htmlFor="affiliation-filter"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Affiliation
              </label>
              <select
                id="affiliation-filter"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                value={filterAffiliation}
                onChange={(e) => {
                  setFilterAffiliation(e.target.value);
                  if (fetchPage) fetchPage(1, perPage);
                }}
              >
                <option value="">Toutes les affiliations</option>
                {uniqueAffiliations.map((aff) => (
                  <option value={aff} key={aff}>
                    {aff}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label
                htmlFor="actions-filter"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Actions
              </label>
              <div className="flex gap-2">
                <button
                  className="flex-1 rounded-md bg-[#57A863] px-3 py-2 text-sm font-medium text-white hover:bg-[#469354] focus:outline-none focus:ring-2 focus:ring-[#57A863]"
                  onClick={() => fetchPage && fetchPage(1, perPage)}
                >
                  Appliquer
                </button>
                <button
                  className="flex-1 rounded-md bg-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  onClick={() => {
                    setFilterAffiliation("");
                    setSearchTerm("");
                    if (fetchPage) fetchPage(1, perPage);
                  }}
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
          {(searchTerm || filterAffiliation) && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm font-medium text-gray-600">
                  Filtres actifs:
                </span>
                {searchTerm && (
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
                    Recherche: "{searchTerm}"
                    <button
                      className="ml-1 text-blue-600 hover:text-blue-800"
                      onClick={() => setSearchTerm("")}
                    >
                      <MdClose className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filterAffiliation && (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                    Affiliation: {filterAffiliation}
                    <button
                      className="ml-1 text-green-600 hover:text-green-800"
                      onClick={() => setFilterAffiliation("")}
                    >
                      <MdClose className="h-3 w-3" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="container mx-auto space-y-6 px-4 py-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rélecteurs</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gérez les rélecteurs et leurs informations
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm">
            <button
              onClick={() => setViewMode("grid")}
              className={`flex items-center px-3 py-2 text-sm font-medium transition-colors sm:px-4 ${viewMode === "grid"
                ? "bg-[#57A863] text-white shadow-sm"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              title="Vue en grille"
            >
              <MdViewModule className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Grille</span>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center px-3 py-2 text-sm font-medium transition-colors sm:px-4 ${viewMode === "list"
                ? "bg-[#57A863] text-white shadow-sm"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              title="Vue en liste"
            >
              <MdViewList className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Liste</span>
            </button>
          </div>
          <button
            onClick={isOpen}
            className="flex items-center rounded-lg bg-gradient-to-r from-[#57A863] to-[#3d9448] px-3 py-2 text-sm font-medium text-white shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 sm:px-4"
          >
            <MdAdd className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Ajouter un rélecteur</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
        </div>
      </div>
      {renderSearchAndFilterControls()}
      {getMainContent()}
    </div>
  );
};

CardUsers.propTypes = {
  isOpen: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
  currentPage: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  lastPage: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  fetchPage: PropTypes.func.isRequired,
};

export default CardUsers;
